import { useMutation } from '@apollo/react-hooks'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonLoading,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar
} from '@ionic/react'
import { home } from 'ionicons/icons'
import React, { useState } from 'react'
import { connect } from "react-redux"
import { RouteComponentProps } from "react-router"
import { AdditionalCard } from '../../components/card/additional.card'
import { AddressCard } from '../../components/card/address.card'
import { BuildingCard } from '../../components/card/building.card'
import { PersonCard } from '../../components/card/person.card'
import EigenheimSteps from "../../components/eigenheim.steps"
import { AdditionalBuilding } from '../../components/form/additional.building.card'
import { Person } from "../../components/form/person.card"
import Note from "../../components/note"
import {
  CREATE_QUOTE_MUTATION,
  CreateQuoteData,
  CreateQuoteVariables
} from '../../graphql/mutation/create.quote'
import { State } from '../../store'
import { createHistoryEntry, createHistoryEntryType } from '../../store/history'
import { addNotification, addNotificationType } from '../../store/notification'
import { addMessage, addMessageType } from '../../store/peter'

interface StateProps {
  codeId: string
  decoded: any;
  address: any;
  person: any;
  building: any;
  paymentTerm: string
  additional: {
    pool: boolean
    horses: any
    dogs: any
    people: Person[]
    buildings: AdditionalBuilding[]
  };
}

interface DispatchProps {
  createHistoryEntry: createHistoryEntryType
  addNotification: addNotificationType
  addMessage: addMessageType
}

type Props = RouteComponentProps<{}> & StateProps & DispatchProps

const Summary: React.FC<Props> = (props) => {
  React.useEffect(() => {
    if (!props.decoded) {
      props.history.push({ pathname: '/home' })
    } else {
      props.addMessage({
        animation: 'pointLeft',
        message: 'Nachdem sie ihre Eingaben geprüft haben können sie ein Angebot anfordern'
      })
    }
  }, [props.decoded])

  const [confirmQuote, setConfirmQuote] = useState(false)
  const [showLoading, setShowLoading] = useState(false);
  const [start] = useMutation<CreateQuoteData, CreateQuoteVariables>(CREATE_QUOTE_MUTATION)

  const confirm = async () => {
    setShowLoading(true);

    const building: any = {}
    Object.keys(props.building).forEach((key) => {
      if (props.building[key]) {
        building[key] = parseInt(props.building[key], 10)
      }
    })

    const additional: any = {
      ...props.additional,
    }

    if (additional.people) {
      additional.people = additional.people.map((p: any) => {
        delete p.id
        return p
      })
    }

    if (additional.buildings) {
      additional.buildings = additional.buildings.map((p: any) => {
        delete p.id
        return p
      })
    }

    if (!additional.dogs) {
      delete additional.dogs
    }

    if (!additional.horses) {
      delete additional.horses
    }

    try {
      const data = {
        type: 'fertigteilhausversicherung',
        paymentTerm: props.paymentTerm,
        person: props.person,
        address: props.address,
        building,
        additional,
      }

      const result = await start({
        variables: {
          code: props.codeId,
          quote: data,
        }
      })

      if (!result.data) {
        throw new Error('failed to create')
      }

      await Promise.all([
        props.createHistoryEntry({
          ...result.data.quote,

          created: Date.now(),
          data
        }),
      ])

      props.addMessage({
        animation: 'convince',
        message: 'Wir verarbeiten nun ihre Daten und stellen ein Personalisiertes Angebot zusammen'
      })

      props.history.push('/anfrage/' + result.data.quote.id)
    } catch (e) {
      props.addNotification({
        color: 'warning',
        message: 'Bitte versuchen sie es später erneut'
      })
    }

    setShowLoading(false);
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color={'primary'}>
          <IonButtons slot="start">
            <IonButton onClick={() => props.history.push({ pathname: '/home' })}>
              <IonIcon slot="icon-only" icon={home} />
            </IonButton>
          </IonButtons>
          <IonTitle>Eigenheim Versicherung</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={'Verarbeite...'}
        />

        <EigenheimSteps current={4}/>

        <PersonCard person={props.person} onEdit={() => props.history.push({ pathname: '/person' })} />
        <AddressCard address={props.address} onEdit={() => props.history.push({ pathname: '/address' })} />
        <BuildingCard additional={props.additional} building={props.building} onEdit={() => props.history.push({ pathname: '/building' })} />
        <AdditionalCard additional={props.additional} paymentTerm={props.paymentTerm} onEdit={() => props.history.push({ pathname: '/additional' })} />

        <Note>Bitte prüfen sie die hier gezeigten Daten bevor sie diese Bestätigen</Note>

        <IonModal
          isOpen={confirmQuote}
          onDidDismiss={() => setConfirmQuote(false)}>
          <IonHeader translucent>
            <IonToolbar color={'primary'}>
              <IonTitle>Angebot einholen</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setConfirmQuote(false)}>Abbrechen</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p>
              Mit dem Klick auf "Jetzt Einholen" werden ihre Daten zu uns übertragen. Wir verabeiten diese und stellen ihnen ein
              personalisiertes Angebot zur Verfügung.
            </p>
            <p>
              Der Erhalt des Angebots stellt <strong>keinen</strong> Versicherungsschutz dar!
            </p>
            <p>
              Die Erstellung des Angebots kann ein paar Minuten dauern, bitte haben sie herfür geduld. Sie erhalten dieses Angebot auch per E-Mail an die von ihnen angegebene Adresse.
            </p>
            <IonButton
              color="primary"
              expand={'block'}
              onClick={() => {
                setConfirmQuote(false)
                confirm()
              }}
            >
              Jetzt Einholen
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => props.history.push({ pathname: '/additional' })}>
              Zurück
            </IonButton>
          </IonButtons>
          <IonButton color="primary" expand={'block'} onClick={() => setConfirmQuote(true)}>weiter</IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default connect<StateProps, DispatchProps, {}, State>(
  ({scan, quote}) => {
    return {
      decoded: scan.decoded,
      person: quote.person,
      address: quote.address,
      building: quote.building,
      additional: quote.additional,
      paymentTerm: quote.paymentTerm,
      codeId: scan.id!
    }
  },
  {
    createHistoryEntry,
    addNotification,
    addMessage,
  }
)(Summary);

